'use client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePointInfo } from '@/hooks/use-point-info';
import {
  CircleCheckIcon,
  CoinsIcon,
  Loader2Icon,
  TriangleAlertIcon,
  WalletIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useMemo } from 'react';

const StartTaskModal = ({
  title,
  isOpen,
  onOpenChange,
  loading,
  onStart,
  offsetPoint,
}: {
  title: string;
  isOpen: boolean;
  onOpenChange: () => void;
  loading: boolean;
  onStart: () => void;
  offsetPoint: number;
}) => {
  const { info, getInfo } = usePointInfo();

  const canStart = useMemo(() => {
    return (
      info.available >= info.task_estimated_point_deep_research + offsetPoint
    );
  }, [info]);

  useEffect(() => {
    if (isOpen) getInfo();
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && canStart && !loading) {
        e.preventDefault();
        onStart();
      }
    },
    [canStart, loading, onStart],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-card" onKeyDown={handleKeyDown}>
        <DialogHeader className="flex flex-col gap-1">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4">
            {canStart ? (
              <CircleCheckIcon className="w-12 h-12 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <TriangleAlertIcon className="w-12 h-12 text-blue-600 dark:text-blue-400" />
            )}
          </div>
          <DialogTitle className="tracking-tight text-xl font-semibold text-foreground text-center">
            {canStart ? `"${title}"任务即将开始！` : '积分不足，无法启动新任务'}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 p-3 bg-secondary text-secondary-foreground rounded-xl">
            <CircleCheckIcon className="w-5 h-5" />
            <span className="text-sm">
              您正在运行 {info?.task_count_deep_research}{' '}
              个深度研究任务，即将启动 1 个新任务。
            </span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-secondary text-secondary-foreground rounded-xl mt-3">
            <CoinsIcon className="w-5 h-5 text-amber-600 shrink-0 dark:text-amber-300" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              这次任务,需要您的积分余额不少于
              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 mx-1 text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-300">
                {info?.task_estimated_point_deep_research}
              </div>
              才能开始
            </span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-secondary text-secondary-foreground rounded-xl mt-3">
            <WalletIcon className="w-5 h-5 text-blue-600 shrink-0 dark:text-blue-400" />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              您当前积分余额：
              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent text-primary-foreground  mx-1 bg-primary">
                {info.available}
              </div>{' '}
              积分。
            </span>
          </div>

          {canStart ? (
            <p className="text-sm text-primary text-center mt-4 p-3 bg-primary-50 rounded-xl dark:bg-neutral-800">
              任务结束后，我们将按实际消耗结算，您的积分最低扣至0。
            </p>
          ) : (
            <div className="text-sm text-foreground gap-x-2 flex items-start mt-4 p-3 bg-primary-50 rounded-xl dark:bg-neutral-800">
              <TriangleAlertIcon className="h-4 w-4 text-primary shrink-0 mt-1" />
              <div className="text-sm text-primary dark:text-blue-200">
                积分余额低于要求，无法启动新任务。请获得更多积分后重试。
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange()} variant="ghost">
            取消
          </Button>
          {canStart ? (
            <Button disabled={loading} onClick={() => onStart()}>
              {loading && <Loader2Icon className="animate-spin w-4 h-4" />}
              确认启动
            </Button>
          ) : (
            <Button disabled={loading} asChild>
              <Link href="/pricing/credits">前往充值</Link>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StartTaskModal;
